/**
 * Waiting-for-opponent panel. Creator sees a big share link + copy
 * button — this is the creator's main UX beat before combat.
 *
 * data-testid contract:
 *   • share-link
 *   • share-copy
 *   • waiting-panel
 */

import { useCallback, useState, type ReactElement } from "react";
import { useStore } from "../store/context.js";
import { useWsClient } from "../net/WsClientProvider.js";
import { navigate } from "../router/hashRoute.js";

/**
 * Wrap the browser Clipboard API with a manual-copy fallback (select
 * the visible text). Returns true on success. Kept exported so tests
 * can mock the API without a whole UI mount.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.clipboard !== "undefined"
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to manual fallback below
  }
  return false;
}

export function WaitingPanel(): ReactElement {
  const { state, dispatch } = useStore();
  const client = useWsClient();
  const [copied, setCopied] = useState(false);
  const joinUrl = state.joinUrl;

  const onCopy = useCallback(() => {
    if (joinUrl === null) return;
    void copyToClipboard(joinUrl).then((ok) => {
      setCopied(ok);
    });
  }, [joinUrl]);

  return (
    <div
      data-testid="waiting-panel"
      style={{
        position: "absolute",
        top: "40%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        padding: "32px 48px",
        background: "#161c24",
        border: "1px solid #444",
        borderRadius: 8,
        maxWidth: "80%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 16,
      }}
    >
      <h2 style={{ margin: 0 }}>Waiting for opponent</h2>
      <p style={{ opacity: 0.7, margin: 0 }}>Share this link:</p>
      <input
        readOnly
        value={joinUrl ?? ""}
        data-testid="share-link"
        onFocus={(e) => {
          e.currentTarget.select();
        }}
        style={{
          padding: "8px 12px",
          fontSize: 16,
          width: "100%",
          borderRadius: 4,
          border: "1px solid #444",
          background: "#0b0f14",
          color: "#dfe6ef",
        }}
      />
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          data-testid="share-copy"
          onClick={onCopy}
          disabled={joinUrl === null}
          style={{
            padding: "8px 16px",
            background: "#5680d6",
            color: "#fff",
            border: "none",
            borderRadius: 4,
          }}
        >
          {copied ? "Copied!" : "Copy link"}
        </button>
        <button
          type="button"
          data-testid="waiting-cancel"
          onClick={() => {
            // #332: tell the server to release the room BEFORE navigating
            // away, then reset the client-side match identity the same
            // way every other exit path does (Home/WinnerBanner/JoinScreen
            // dispatch `match.leave`). Previously this only navigated —
            // the server kept `state.room` bound forever, poisoning the
            // connection for any future create/join/quickJoin.
            client.leaveMatch();
            dispatch({ type: "match.leave" });
            navigate({ kind: "home" });
          }}
          style={{ padding: "8px 12px" }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
